import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Rocket, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { finalizeAll } from "@/lib/imports";
import { useDraft } from "@/components/import/DraftContext";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function CancelImport() {
  const { id } = useDraft();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const cancel = async () => {
    try {
      await api.post(`/imports/${id}/cancel`);
      navigate("/import");
      qc.removeQueries({ queryKey: ["imports", id] });
      qc.invalidateQueries({ queryKey: ["imports", "list"] });
      toast.success("Import cancelled");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild><button className="btn-danger" data-testid="import-cancel-button"><Trash2 className="h-4 w-4" />Cancel import</button></AlertDialogTrigger>
      <AlertDialogContent className="border-[#2A3447] bg-[#121620] text-slate-200">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading text-amber-100">Cancel this import?</AlertDialogTitle>
          <AlertDialogDescription>Cards not yet imported and their unused images are deleted. Cards already imported stay in your library.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="import-cancel-keep">Keep draft</AlertDialogCancel>
          <AlertDialogAction onClick={cancel} className="bg-red-700 hover:bg-red-600" data-testid="import-cancel-confirm">Cancel import</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function FinalizePanel() {
  const { draft, id, refresh, open } = useDraft();
  const qc = useQueryClient();
  const [partial, setPartial] = useState(false);
  const [run, setRun] = useState(null);
  const stop = useRef(false);
  const s = draft.summary;
  const blocked = s.errors + s.missing_images;
  const pkgBusy = ["pending", "processing"].includes(draft.package?.status);

  const go = async () => {
    stop.current = false;
    setRun({ created: 0 });
    try {
      const res = await finalizeAll(id, partial, (_, created) => setRun({ created }), () => stop.current);
      toast.success(`${res.created} card(s) imported${res.failed.length ? ` · ${res.failed.length} could not be saved` : ""}`);
      qc.invalidateQueries({ queryKey: ["cards"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRun(null);
      refresh();
    }
  };
  const toggleSet = async (v) => {
    try {
      await api.patch(`/imports/${id}`, { update_set_details: v });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!open) return <div className="panel p-6 text-sm text-slate-300" data-testid="import-finished">This import is finished: {s.finalized} imported, {s.skipped} skipped.</div>;
  return (
    <div className="panel space-y-5 p-6" data-testid="import-finalize-panel">
      <p className="eyebrow">Import destination</p>
      <ul className="space-y-1.5 text-sm text-slate-300">
        <li>Set: {draft.set ? <strong className="text-amber-200">{draft.set.code} — {draft.set.name}</strong> : "each card's own set code (or none)"} · new sets are created private</li>
        <li data-testid="import-privacy-note">Privacy: new cards are imported as <strong className="text-amber-200">Private</strong>. Publish them later from each card or set. Updated cards keep their current privacy.</li>
        <li>Only cards with every required face image can be imported. Text inside images is not read or checked by the site.</li>
      </ul>
      {draft.set && (
        <label className="flex items-center gap-3 text-sm text-slate-300">
          <Switch checked={draft.set_options?.update_details} onCheckedChange={toggleSet} data-testid="import-update-set-switch" />
          If I already have set {draft.set.code}, update its name, description and details
        </label>
      )}
      {blocked > 0 && s.ready > 0 && (
        <label className="flex items-start gap-3 rounded border border-amber-600/40 bg-amber-950/20 p-3 text-sm text-amber-200">
          <input type="checkbox" className="mt-1" checked={partial} onChange={(e) => setPartial(e.target.checked)} data-testid="import-partial-checkbox" />
          Partial import: import the {s.ready} complete card(s) now and keep the {blocked} card(s) that need images or fixes in this draft.
        </label>
      )}
      {run && (
        <div className="space-y-1" data-testid="import-finalize-progress">
          <p className="font-mono text-xs text-slate-400">Imported {run.created} of {s.ready + run.created}…</p>
          <div className="h-1.5 overflow-hidden rounded-full bg-[#2A3447]"><div className="h-full bg-amber-500 transition-[width]" style={{ width: `${(run.created / Math.max(s.ready + run.created, 1)) * 100}%` }} /></div>
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        <button className="btn-gold" onClick={go} disabled={Boolean(run) || pkgBusy || !s.ready || (blocked > 0 && !partial)} data-testid="import-finalize-button">
          {run ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}Import {s.ready} card{s.ready === 1 ? "" : "s"}
        </button>
        {run && <button className="btn-ghost" onClick={() => { stop.current = true; }} data-testid="import-finalize-stop">Pause after this batch</button>}
        <CancelImport />
      </div>
    </div>
  );
}
